import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { CheckCircle, MapPin } from 'lucide-react';
import { formatShortLocation } from '@/utils/location';
import { getTypeLabel } from '@/constants/entityTypes';

export const UserEntitiesModal = ({
  open,
  onOpenChange,
  selectedUser,
  allEntities,
  selectedUserEntities,
  onToggleUserEntity,
  onSave,
  onCancel,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage User Partners</DialogTitle>
          <DialogDescription>
            Select which entities {selectedUser?.name || 'this user'} can manage
          </DialogDescription>
        </DialogHeader>

        {selectedUser && (
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm">
                <strong>Selected: {selectedUserEntities.length} entities</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                This user will be able to manage all selected entities from their dashboard. Some entities may be skipped if they already have 2 admins.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Available Partners ({allEntities.length})</Label>
              {allEntities.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4 text-center">Loading entities...</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto p-2">
                  {allEntities.map((entity) => {
                    const isSelected = selectedUserEntities.includes(entity.entity_id);
                    return (
                      <div
                        key={entity.entity_id}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => onToggleUserEntity(entity.entity_id)}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}
                          >
                            {isSelected && <CheckCircle className="h-4 w-4 text-primary-foreground" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{entity.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {getTypeLabel(entity.entity_type)}
                            </p>
                            {entity.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="h-3 w-3" />
                                {formatShortLocation(entity)}
                              </p>
                            )}
                            {entity.entity_admins && entity.entity_admins.length > 0 && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                {entity.entity_admins.length}/2 admins
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSave} disabled={!selectedUser || allEntities.length === 0}>
            Save ({selectedUserEntities.length} selected)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserEntitiesModal;
